import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Checkbox, Input, Modal, Upload } from 'antd';
import { CameraOutlined, DownOutlined, InboxOutlined, MessageOutlined, UpOutlined } from '@ant-design/icons';
import {
  createOrderGenieRequest,
  processOrderGenieRequestAsync,
  updateOrderGenieSummary,
} from '../../services/orderXApi';
import { setStoredPendingRequest } from '../../pages/orderXV4Utils';
import ProcessingStatusCard from './ProcessingStatusCard';
import CustomerShipToContactFields from './CustomerShipToContactFields';
import CustomerShipToContactCards from './CustomerShipToContactCards';
import DocumentScanPreview from './DocumentScanPreview';

const TABULAR_NAME_RE = /\.(csv|xlsx?|xls)$/i;
const isTabularFile = (file) => TABULAR_NAME_RE.test(file?.name || '')
  || /spreadsheet|excel|csv/.test(String(file?.type || '').toLowerCase());

const { Dragger } = Upload;

const NOTIFY_EMAIL_STORAGE_KEY = 'order_genie_v4_notify_email';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const buildDefaultName = () => {
  const now = new Date();
  const pad = (v) => String(v).padStart(2, '0');
  return `Request ${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
};

const StepIntake = ({
  userId, onProcessed, formValues, onFormValuesChange, onCustomerSelected, erpConnector = null,
}) => {
  const [requestName, setRequestName] = useState(buildDefaultName());
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [activeRequest, setActiveRequest] = useState(null); // { requestId, requestName }
  const [dismissedToBackground, setDismissedToBackground] = useState(false);
  const [error, setError] = useState('');
  const [pickerExpanded, setPickerExpanded] = useState(false);
  const [notifyEnabled, setNotifyEnabled] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState(() => {
    try { return localStorage.getItem(NOTIFY_EMAIL_STORAGE_KEY) || ''; } catch (err) { return ''; }
  });
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const pasteInputRef = useRef(null);

  const stopCameraStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => () => stopCameraStream(), [stopCameraStream]);

  const openCamera = useCallback(async () => {
    setCameraError('');
    setCameraOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      setCameraError(err?.message || 'Unable to access camera.');
    }
  }, []);

  const closeCamera = useCallback(() => {
    stopCameraStream();
    setCameraOpen(false);
  }, [stopCameraStream]);

  const capturePhoto = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setFiles((prev) => [...prev, file]);
      closeCamera();
    }, 'image/jpeg', 0.92);
  }, [closeCamera]);

  const handlePasteClick = useCallback(() => {
    pasteInputRef.current?.focus();
  }, []);

  const handlePasteEvent = useCallback((e) => {
    const items = e.clipboardData?.items || [];
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (item.type && item.type.startsWith('image/')) {
        const blob = item.getAsFile();
        if (blob) {
          const file = new File([blob], `pasted-image-${Date.now()}.png`, { type: blob.type });
          setFiles((prev) => [...prev, file]);
          e.preventDefault();
          return;
        }
      }
    }
    const text = e.clipboardData?.getData('text/plain');
    if (text && text.trim()) {
      const file = new File([text], `pasted-text-${Date.now()}.txt`, { type: 'text/plain' });
      setFiles((prev) => [...prev, file]);
      e.preventDefault();
    }
  }, []);

  const removeFile = useCallback((name) => {
    setFiles((prev) => prev.filter((f) => f.name !== name));
  }, []);

  // Object URLs for the just-attached local files so Step 1 can show the actual
  // document (with the scanning overlay) before it's even uploaded, mirroring
  // v3's doc-scan-wrap effect. Revoked whenever the file list changes/unmounts.
  const [fileObjectUrls, setFileObjectUrls] = useState({});
  useEffect(() => {
    const urls = {};
    files.forEach((f) => { urls[f.name] = URL.createObjectURL(f); });
    setFileObjectUrls(urls);
    return () => { Object.values(urls).forEach((u) => URL.revokeObjectURL(u)); };
  }, [files]);

  const runStart = useCallback(async () => {
    setError('');
    setSubmitting(true);
    setDismissedToBackground(false);
    try {
      const finalRequestName = requestName.trim() || buildDefaultName();
      const createResp = await createOrderGenieRequest({
        requestName: finalRequestName,
        userId,
        customerIdAsInErp: formValues?.customer,
        files,
        // The creation-time notification (default true server-side) fires immediately,
        // before matching runs — misleading given the "ready to review" copy it sends.
        // V4 instead opts into a completion-time email below via process-async.
        notifyByEmail: false,
        notifyBySms: false,
      });
      const newRequestId = createResp?.request?.id;
      if (!newRequestId) {
        throw new Error('Unable to create request.');
      }
      // customer_id_erp is already persisted by create_request above; ship-to/
      // contact/bill-to have no creation-time field, so merge them in separately
      // (best-effort — mirrors OrderXV4.js's header-fields autosave pattern).
      if (String(formValues?.ship_to || '').trim() || String(formValues?.contact || '').trim()) {
        updateOrderGenieSummary(newRequestId, {
          user_id: userId,
          summary: {
            ship_to: formValues?.ship_to,
            ship_to_details: formValues?.ship_to_details,
            bill_to_details: formValues?.bill_to_details,
            contact: formValues?.contact,
            contact_details: formValues?.contact_details,
          },
          merge: true,
        }).catch(() => { /* best-effort — Step 3 can still capture/edit these */ });
      }
      if (notifyEnabled && notifyEmail.trim()) {
        try { localStorage.setItem(NOTIFY_EMAIL_STORAGE_KEY, notifyEmail.trim()); } catch (err) { /* ignore */ }
      }
      await processOrderGenieRequestAsync(newRequestId, {
        user_id: userId,
        notify_on_complete_email: notifyEnabled,
        notify_email: notifyEnabled ? notifyEmail.trim() : '',
      });
      setStoredPendingRequest(newRequestId, userId, finalRequestName);
      setActiveRequest({ requestId: newRequestId, requestName: finalRequestName });
    } catch (err) {
      setError(err?.message || 'Unable to process request.');
      setSubmitting(false);
    }
  }, [files, requestName, userId, notifyEnabled, notifyEmail, formValues]);

  const handleStart = useCallback(() => {
    if (!files.length) {
      setError('Add at least one file, photo, or pasted item to start.');
      return;
    }
    if (notifyEnabled && !EMAIL_RE.test(notifyEmail.trim())) {
      setError('Enter a valid email address to be notified, or turn that option off.');
      return;
    }
    const selectedCustomer = String(formValues?.customer || '').trim();
    if (!selectedCustomer) {
      Modal.confirm({
        title: 'Continue without a customer?',
        content: 'Selecting a customer now helps match items by customer part number '
          + '(if present) and shows accurate pricing right away. You can still pick one '
          + 'later in Step 3.',
        okText: 'Skip & Continue',
        cancelText: 'Select Customer',
        onOk: () => { runStart(); },
        onCancel: () => { setPickerExpanded(true); },
      });
      return;
    }
    // Always confirm the customer before kicking off processing — a value here may
    // just be carried over from a previous request's "last used" default rather
    // than something actively picked for this one, so ask rather than assume.
    const customerLabel = formValues.customer_search || selectedCustomer;
    Modal.confirm({
      title: 'Use this customer?',
      content: `This request will be processed for customer "${customerLabel}". `
        + 'Continue with this customer, or change it first?',
      okText: 'Confirm & Continue',
      cancelText: 'Change Customer',
      onOk: () => { runStart(); },
      onCancel: () => { setPickerExpanded(true); },
    });
  }, [files, notifyEnabled, notifyEmail, formValues, runStart]);

  if (activeRequest && dismissedToBackground) {
    return (
      <Card className="shadow-sm border-0 text-center py-5">
        <h5 className="mb-2">Request #{activeRequest.requestId} is still processing</h5>
        <p className="text-muted mb-3">
          It's running in the background on our server.
          {notifyEnabled ? " We'll email you as soon as it's ready to review." : ' Come back anytime to check on it.'}
        </p>
        <div className="d-flex justify-content-center gap-2">
          <Button type="primary" onClick={() => setDismissedToBackground(false)}>View progress</Button>
        </div>
      </Card>
    );
  }

  if (activeRequest) {
    return (
      <ProcessingStatusCard
        requestId={activeRequest.requestId}
        userId={userId}
        requestName={activeRequest.requestName}
        onReady={(fullRequest) => onProcessed(activeRequest.requestId, fullRequest)}
        onFailed={(message) => {
          setError(message);
          setSubmitting(false);
          setActiveRequest(null);
        }}
        onLeaveInBackground={() => setDismissedToBackground(true)}
        onCancelled={() => {
          setSubmitting(false);
          setActiveRequest(null);
        }}
      />
    );
  }

  return (
    <Card className="shadow-sm border-0">
      <h4 className="mb-2">Start a new quote/order request</h4>
      <p className="text-muted mb-3">
        Upload one or more documents, paste text/image, or snap a photo. We will extract line items and match products.
      </p>
      <div className="mb-3">
        <label className="form-label">Request Name</label>
        <Input value={requestName} onChange={(e) => setRequestName(e.target.value)} />
      </div>

      <div className="mb-3 p-3 bg-light rounded">
        <div
          role="button"
          tabIndex={0}
          className="d-flex justify-content-between align-items-center mb-2"
          onClick={() => setPickerExpanded((prev) => !prev)}
          onKeyDown={(e) => { if (e.key === 'Enter') setPickerExpanded((prev) => !prev); }}
        >
          <div>
            <span className="fw-semibold">Customer &amp; Shipping (optional)</span>
            {!String(formValues?.customer || '').trim() && (
              <div className="text-muted small">
                Select now to speed up matching &amp; pricing, or skip and pick it in Step 3.
              </div>
            )}
          </div>
          <Button type="link" size="small" icon={pickerExpanded ? <UpOutlined /> : <DownOutlined />}>
            {pickerExpanded ? 'Hide' : 'Select Customer'}
          </Button>
        </div>
        <CustomerShipToContactCards formValues={formValues} />
        {pickerExpanded && (
          <CustomerShipToContactFields
            formValues={formValues}
            onFormValuesChange={onFormValuesChange}
            onCustomerSelected={onCustomerSelected}
            erpConnector={erpConnector}
          />
        )}
      </div>

      <div className="row g-3 mb-3">
        <div className="col-12 col-md-6">
          <Dragger
            multiple
            fileList={[]}
            beforeUpload={(file) => {
              setFiles((prev) => [...prev, file]);
              return false;
            }}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Click or drag file(s) here</p>
            <p className="ant-upload-hint text-muted small">PDF, Excel, CSV, images — multiple files supported.</p>
          </Dragger>
        </div>
        <div className="col-6 col-md-3">
          <Button block size="large" icon={<CameraOutlined />} onClick={openCamera} style={{ height: '100%' }}>
            Take Photo
          </Button>
        </div>
        <div className="col-6 col-md-3">
          <Button block size="large" icon={<MessageOutlined />} onClick={handlePasteClick} style={{ height: '100%' }}>
            Paste
          </Button>
          <textarea
            ref={pasteInputRef}
            onPaste={handlePasteEvent}
            style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }}
            aria-hidden="true"
          />
        </div>
      </div>
      {files.length > 0 && (
        <div className="mb-3">
          <div className="fw-semibold small mb-2">Attached ({files.length})</div>
          <div className="row g-2">
            {files.map((f) => (
              <div className="col-6 col-md-4 col-lg-3" key={f.name}>
                <div className="border rounded p-2 h-100 d-flex flex-column">
                  <DocumentScanPreview
                    previewUrl={fileObjectUrls[f.name]}
                    previewType={f.type}
                    filename={f.name}
                    scanning={false}
                    tabular={isTabularFile(f)}
                    minHeight={140}
                  />
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <span className="small text-truncate" style={{ maxWidth: '80%' }} title={f.name}>{f.name}</span>
                    <span role="button" className="text-danger" onClick={() => removeFile(f.name)}>×</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="mb-3 p-3 bg-light rounded">
        <Checkbox checked={notifyEnabled} onChange={(e) => setNotifyEnabled(e.target.checked)}>
          Email me when this request is ready for review
        </Checkbox>
        {notifyEnabled && (
          <div className="mt-2" style={{ maxWidth: 360 }}>
            <Input
              type="email"
              placeholder="you@company.com"
              value={notifyEmail}
              onChange={(e) => setNotifyEmail(e.target.value)}
            />
            <div className="text-muted small mt-1">
              We'll send a link straight to this request once matching is done — no need to keep this tab open.
            </div>
          </div>
        )}
      </div>
      {error && <div className="alert alert-danger py-2">{error}</div>}
      <Button type="primary" size="large" onClick={handleStart} loading={submitting} disabled={!files.length}>
        Start Request
      </Button>

      <Modal
        title="Take a Photo"
        open={cameraOpen}
        onCancel={closeCamera}
        footer={[
          <Button key="cancel" onClick={closeCamera}>Cancel</Button>,
          <Button key="capture" type="primary" onClick={capturePhoto}>Capture</Button>,
        ]}
      >
        {cameraError ? (
          <div className="alert alert-danger">{cameraError}</div>
        ) : (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video ref={videoRef} style={{ width: '100%', borderRadius: 8 }} muted playsInline />
        )}
      </Modal>
    </Card>
  );
};

export default StepIntake;
